/* The locale, and the `t` every component uses.
 *
 * Resolution order is `i18n.md`'s: explicit user preference → account setting →
 * `Accept-Language` → source. On these screens there is no account yet, so it
 * starts at the browser's languages. Once somebody signs in, their stored
 * `users.locale` wins — that is what the `locale` prop is for.
 *
 * Locale is a user setting, never a URL accident and never inferred from
 * country: a Turkish speaker in Germany wants Turkish.
 */

import { createTranslator, type Locale, resolveLocale, type Translator } from '@eventuley/i18n';
import type { ReactNode } from 'react';
import { createContext, useContext, useEffect, useMemo } from 'react';
import { formatLocaleFor } from './format-locale.js';

interface I18n {
  /** The catalogue's locale — which language the strings are in. */
  readonly locale: Locale;
  /**
   * The locale that formats dates, numbers and money.
   *
   * NOT the same as `locale`, and the difference is easy to miss: the
   * catalogue has `en`, and bare `en` formats the American way. A reader in
   * London gets English strings and British ordering; the language comes from
   * us, the region from them.
   */
  readonly formatLocale: string;
  readonly t: Translator;
}

/* Exported so the pseudo-locale verification can mount screens with a
 * translator of its own — `i18n.md` requires that check on every screen. The
 * alternative was a test-only prop on `I18nProvider`, which puts verification
 * machinery in the component every screen renders. */
export const I18nContext = createContext<I18n | null>(null);

/** Picks the first of the browser's languages that we support. */
export function localeFromBrowser(languages: readonly string[]): Locale {
  for (const language of languages) {
    const resolved = resolveLocale(language);
    /* `resolveLocale` returns the source locale for anything unsupported, so a
     * bare match would stop at the first entry every time. Only a genuine hit
     * ends the search; the source locale is the fallback after the loop. */
    if (resolved !== 'en' || language.toLocaleLowerCase('en-US').startsWith('en')) {
      return resolved;
    }
  }
  return 'en';
}

export interface I18nProviderProps {
  /** Overrides the browser. Set from the signed-in User's stored preference. */
  readonly locale?: Locale;
  readonly children: ReactNode;
}

export function I18nProvider({ locale, children }: I18nProviderProps) {
  const resolved = useMemo(
    () => locale ?? localeFromBrowser(typeof navigator === 'undefined' ? [] : navigator.languages),
    [locale],
  );

  useEffect(() => {
    /* `lang` on the document, so a screen reader pronounces the page in the
     * right language and the browser hyphenates it correctly — `i18n.md`. */
    document.documentElement.lang = resolved;
  }, [resolved]);

  /* Read from the browser rather than from `resolved`, because the region is
   * the reader's and the language is ours. */
  const formatLocale = useMemo(
    () => formatLocaleFor(resolved, typeof navigator === 'undefined' ? [] : navigator.languages),
    [resolved],
  );

  const value = useMemo<I18n>(
    () => ({
      locale: resolved,
      formatLocale,
      t: createTranslator(resolved, (key) => {
        /* A gap in a target locale falls back and says so. In the source
         * locale it throws, and the key type makes it unreachable anyway. */
        console.warn(`[i18n] "${key}" is missing in "${resolved}"; used English.`);
      }),
    }),
    [resolved, formatLocale],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18n {
  const value = useContext(I18nContext);
  if (!value) throw new Error('useI18n must be used inside an I18nProvider.');
  return value;
}

/** The common case: just the translator. */
export const useTranslate = (): Translator => useI18n().t;
